import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ALL_OPTION_NO_SPACE } from "../../../constants";

interface Option {
  value: string;
  label: string;
}

interface Props {
  options: Option[];
  // Comma-joined selected option values, or the "All" sentinel (AnalyticsPage.tsx's own
  // ALL_OPTION_NO_SPACE) - same plain-string convention every other field's value already uses.
  value: string;
  onChange: (value: string) => void;
  testId: string;
}

// A small, self-contained checkbox-dropdown for the Downloads page's own generic, backend-driven
// filters - lets a multiselect field (market/region/model/...) select more than one value, same as
// every "normal" dashboard's own market/region filter elsewhere in the app. Not a reuse of that
// existing MultiSelectFilter.tsx: this page's filter values live in AnalyticsPage.tsx's own local
// per-dashboard state, not Redux, so that component's dispatch-coupled implementation doesn't fit.
//
// Commits exactly once, via the "Done" button - not per checkbox click - so AnalyticsPage.tsx's
// own debounced auto-refetch-on-change (handleFilterChange) fires once per selection, not once per
// checkbox toggled while picking several.
export const MultiSelectFilterField = (props: Props) => {
  const { options, value, onChange, testId } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [draftSelected, setDraftSelected] = useState<string[]>([]);
  // Where the portaled popover renders, in viewport coordinates - computed from the trigger
  // button's own position (see open() below), since it no longer sits inside .analysis_tile's own
  // box (see the portal comment further down) and so can't just be `position: absolute` relative
  // to it anymore.
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // The popover's own position is computed once, at open() - a `position: fixed` box doesn't
  // track the page scrolling back under it the way the old `position: absolute`-relative-to-tile
  // one did, so left open through a scroll it would visually drift away from its own trigger.
  // Closing on scroll (rather than repositioning on every scroll tick) matches this app's other
  // dropdowns' own "click away to dismiss" convention - scrolling counts as the same kind of
  // "moved on" gesture. Capture phase since scroll events don't bubble - the only way to observe
  // one dispatched on a scrollable element nested arbitrarily deep, like the popover's own options
  // list below. Explicitly excludes scrolls that happen INSIDE the popover itself (via
  // popoverRef.contains) - otherwise scrolling that same options list to see more of them would
  // close the popover on the very first scroll, defeating the reason it scrolls at all.
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (event: Event) => {
      if (popoverRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener("scroll", handleScroll, true);
    return () => document.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  // Reported bug: for the last dashboard's tile on a page with several selected (or any trigger
  // otherwise sitting close to the bottom of the viewport), open()'s own `rect.bottom + 4` always
  // opens the popover downward - with a page this tall, that pushes it (Done button and the last
  // few options included) past the bottom of the viewport with no way to reach it. Portaling to
  // <body> (see the render below) already escapes .analysis_tile's own overflow clip, but doesn't
  // by itself account for the VIEWPORT's own bottom edge - a separate kind of clipping. Runs after
  // the popover has actually painted (useLayoutEffect, not useEffect, so the flip below happens
  // before the browser's next paint - no visible flash at the wrong position first) so it can
  // measure the popover's own real rendered height (respecting its CSS max-height/content) rather
  // than hardcoding a duplicate of that number here.
  useLayoutEffect(() => {
    if (!isOpen || !popoverRef.current || !triggerRef.current) return;
    const popoverHeight = popoverRef.current.getBoundingClientRect().height;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    if (spaceBelow < popoverHeight + 4 && spaceAbove >= popoverHeight + 4) {
      setPopoverPosition((prev) => ({ ...prev, top: triggerRect.top - popoverHeight - 4 }));
    }
  }, [isOpen]);

  const selected = value && value !== ALL_OPTION_NO_SPACE ? value.split(",") : [];

  const open = () => {
    // Seeds the draft from the last committed value, filtered to options that still actually
    // exist (the backend re-derives each field's own option list live - a previously-selected
    // value can drop out of it, e.g. once a market it depended on changes). "All" means every
    // option is implicitly selected, so it seeds every checkbox (including "All" itself) as
    // checked - not every checkbox unchecked, which would misrepresent what "All" actually means.
    const seeded = selected.filter((selectedValue) => options.some((option) => option.value === selectedValue));
    setDraftSelected(seeded.length > 0 ? seeded : options.map((option) => option.value));
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setIsOpen(true);
  };

  const toggleOption = (optionValue: string) => {
    setDraftSelected((prev) => (prev.includes(optionValue) ? prev.filter((v) => v !== optionValue) : [...prev, optionValue]));
  };

  // "All" is a master checkbox, not just another option: checking it checks every real option
  // below (and unchecking it unchecks all of them), and - the reverse direction - it shows itself
  // checked once every real option ends up checked some other way (see isAllChecked/the render
  // below), same two-way sync as any other "select all" checkbox.
  const isAllChecked = draftSelected.length === options.length;
  const selectAll = () => setDraftSelected(isAllChecked ? [] : options.map((option) => option.value));

  const commit = () => {
    // Selecting every real option (or none at all) means the same thing as "All" - collapses to
    // that same sentinel rather than a redundant full list, matching defaultFilterValues/
    // buildRequestFilters' own "All" handling (AnalyticsPage.tsx).
    const isEffectivelyAll = draftSelected.length === 0 || isAllChecked;
    onChange(isEffectivelyAll ? ALL_OPTION_NO_SPACE : draftSelected.join(","));
    setIsOpen(false);
  };

  const triggerLabel =
    selected.length === 0
      ? "All"
      : selected.length === 1
      ? options.find((option) => option.value === selected[0])?.label ?? selected[0]
      : `${selected.length} selected`;

  return (
    <div className="analysis_multiselect">
      <button
        ref={triggerRef}
        type="button"
        className="analysis_multiselect_trigger"
        onClick={open}
        data-test-id={testId}
        aria-expanded={isOpen}
      >
        {triggerLabel}
        <span className="analysis_multiselect_arrow" />
      </button>

      {/* Reported bug: with the options list scrollable but genuinely too tall to fit, some
          entries never became visible no matter how far you scrolled - the popover itself was
          getting clipped by .analysis_tile's own `overflow: hidden` (there for its rounded
          corners - analyticsPage.scss's own comment on that rule) whenever it needed to extend
          past the tile's bottom edge, which a scrollable INSIDE it can't do anything about since
          the clip is on the outside of the whole popover box, not just its content. Portaled to
          <body> so it's no longer a descendant of .analysis_tile at all - genuinely escapes the
          clip instead of just scrolling within it - positioned via the trigger's own real
          coordinates (position: fixed inline styles) rather than CSS position: absolute relative
          to an ancestor it's no longer inside. */}
      {isOpen &&
        createPortal(
          <>
            {/* Closes on any outside click - simpler and more testable than a global document
                listener, and covers the same "click away to dismiss" expectation every other
                dropdown in this app already has. */}
            <div className="analysis_multiselect_backdrop" onClick={() => setIsOpen(false)} data-test-id={`${testId}-backdrop`} />
            <div
              ref={popoverRef}
              className="analysis_multiselect_popover"
              style={{ top: popoverPosition.top, left: popoverPosition.left, width: popoverPosition.width }}
              data-test-id={`${testId}-popover`}
            >
              {/* Scrolls on its own (a long options list - many markets/models/...) while Done
                  stays outside it, always visible below - previously Done was just the last child
                  of the whole popover's own scroll area, so it scrolled out of view/got cropped
                  along with the options once there were enough of them to exceed the popover's
                  max-height. */}
              <div className="analysis_multiselect_options">
                <label className="analysis_multiselect_option">
                  <input type="checkbox" checked={isAllChecked} onChange={selectAll} data-test-id={`${testId}-option-All`} />
                  All
                </label>
                {options.map((option) => (
                  <label className="analysis_multiselect_option" key={option.value}>
                    <input
                      type="checkbox"
                      checked={draftSelected.includes(option.value)}
                      onChange={() => toggleOption(option.value)}
                      data-test-id={`${testId}-option-${option.value}`}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
              <button type="button" className="analysis_multiselect_done" onClick={commit} data-test-id={`${testId}-done`}>
                Done
              </button>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};
